import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Toast, ToastContainer } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import Loader from "../common/Loader.jsx";
import { useGlobal } from "../../GlobalContext";
import { canEdit } from "../common/permissionUtils";
import { getOpportunityViewApi, OPPORTUNITY_STAGES, STAGE_COLORS, updateOpportunityStageApi } from "./opportunity-api";
import Back from "../../assets/svg/back.svg";
import AiSummaryCard from "../common/ai-summary-card.js";
import { getOpportunityAiSummaryApi } from "./opportunity-api";

const OpportunityView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const opportunityId = location.state?.id;

  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Opportunity");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", success: true });

  const fetchOpportunity = useCallback(async () => {
    if (!opportunityId) return;
    setLoading(true);
    try {
      const response = await getOpportunityViewApi(opportunityId);
      if (response?.data?.success) setData(response.data.data);
    } catch (error) {
      console.error("Error fetching opportunity:", error);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    fetchOpportunity();
  }, [fetchOpportunity]);

  const changeStage = async (stage) => {
    const response = await updateOpportunityStageApi(opportunityId, stage);
    setToast({ show: true, message: response?.data?.message ?? "Updated", success: response?.data?.success });
    if (response?.data?.success) fetchOpportunity();
  };

  if (loading) return <Loader height="80vh" />;

  const colors = STAGE_COLORS[data?.stage] || { bg: "#F3F4F6", text: "#546088" };
  const money = `${data?.currency_symbol ?? ""}${data?.amount ?? "0.00"}`;

  const detail = (label, value) => (
    <div>
      <div className="iv-detail-label">{label}</div>
      <div className="iv-detail-value">{value || "--"}</div>
    </div>
  );

  return (
    <div style={{ padding: "16px" }}>
      <ToastContainer position="top-end">
        <Toast bg={toast.success ? "success" : "danger"} show={toast.show} onClose={() => setToast({ ...toast, show: false })} delay={3000} autohide>
          <Toast.Body style={{ color: "#ffffff" }}>{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          <img src={Back} alt="Back" style={{ cursor: "pointer" }} onClick={() => navigate(-1)} />
          <div>
            <div className="fs-20 fw-600 text-dkblue">{data?.title || "Opportunity"}</div>
            <div className="fs-13 fw-500 text-grey">
              {data?.opportunity_code} · {data?.party_name || "--"}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-3">
          <span className="fs-12 fw-600" style={{ backgroundColor: colors.bg, color: colors.text, borderRadius: "100px", padding: "6px 14px" }}>
            {data?.stage}
          </span>
          {hasEditPermission && (
            <>
              <select className="form-control" style={{ width: "auto" }} value={data?.stage ?? ""} onChange={(e) => changeStage(e.target.value)}>
                {OPPORTUNITY_STAGES.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
              <button className="btn-save" onClick={() => navigate("/opportunity/create", { state: { id: opportunityId } })}>
                Edit
              </button>
            </>
          )}
        </div>
      </div>

      <Row>
        <Col md={8}>
          <div className="iv-card mb-3">
            <div className="iv-card-title">Opportunity Details</div>
            <div className="iv-detail-grid">
              {detail("Opportunity No.", data?.opportunity_code)}
              {detail("Type", data?.type)}
              {detail(data?.type === "Lead" ? "Lead" : "Customer", data?.party_name)}
              {detail("Sales Person", data?.sales_person)}
              {detail("Source of Lead", data?.lead_source)}
              {detail("Opportunity Date", data?.opportunity_date)}
              {detail("Expected Close Date", data?.expected_close_date)}
              {detail("Probability", `${data?.probability ?? 0}%`)}
              {detail("Expected Value", money)}
            </div>
          </div>

          <div className="iv-card mb-3">
            <div className="iv-card-title">Contact</div>
            <div className="iv-detail-grid">
              {detail("Email", data?.customer_email)}
              {detail("Phone", data?.customer_phone)}
              {detail("Sales Person Email", data?.sales_person_email)}
            </div>
          </div>

          <div className="iv-card mb-3">
            <div className="iv-card-title">Address</div>
            <div className="iv-detail-grid">
              {detail("Address", data?.address)}
              {detail("City", data?.city)}
              {detail("Country", data?.country)}
              {detail("Postal Code", data?.postal_code)}
            </div>
          </div>

          {data?.description && (
            <div className="iv-card mb-3">
              <div className="iv-card-title">Project Description</div>
              <div className="iv-description">{data.description}</div>
            </div>
          )}
        </Col>

        <Col md={4}>
          <AiSummaryCard
            api={getOpportunityAiSummaryApi}
            module="opportunity"
            id={opportunityId}
            initial={data}
            hint="Score this opportunity on value, stage and how near its close date is, with a suggested next action."
          />

          <div className="iv-card mb-3">
            <div className="iv-card-title">Discussion</div>
            {data?.discussions?.length ? (
              data.discussions.map((row) => (
                <div key={row.id} style={{ borderBottom: "1px solid #f1f2f4", padding: "10px 0" }}>
                  <div className="fs-13 fw-500 text-dkblue">{row.comment}</div>
                  <div className="fs-12 fw-500 text-grey">
                    {row.created_by || "System"} · {row.discussion_date || "--"}
                  </div>
                </div>
              ))
            ) : (
              <div className="fs-13 fw-500 text-grey">No discussion yet.</div>
            )}
          </div>

          <div className="iv-card mb-3">
            <div className="iv-card-title">Attachments</div>
            {data?.attachments?.length ? (
              data.attachments.map((file) => (
                <div key={file.id} style={{ padding: "8px 0" }}>
                  <a className="fs-13 fw-500 textblue" href={file.url} target="_blank" rel="noreferrer">{file.file_name}</a>
                  <div className="fs-12 fw-500 text-grey">{file.created_by || "System"} · {file.date}</div>
                </div>
              ))
            ) : (
              <div className="fs-13 fw-500 text-grey">No attachments.</div>
            )}
          </div>

          <div className="iv-card">
            <div className="iv-card-title">Record Info</div>
            <div className="iv-detail-grid">
              {detail("Created By", data?.created_by)}
              {detail("Created", data?.created)}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default OpportunityView;
